import { SearchField } from "@/components/common/SearchField/SearchField"
import { toast } from "@/components/common/Toast"
import { FacetedFilter } from "@/components/feature/KnowledgebaseFilters/FacetedFilter"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  useTranslation,
  type TranslationKeys,
} from "@/hooks/useTranslation/useTranslation"
import { format, formatDistanceToNow } from "date-fns"
import {
  AlertTriangle,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  Clock,
  Download,
  Filter,
  Pencil,
  Send,
  Trash2,
} from "lucide-react"
import { useEffect, useMemo, useState } from "react"
import { useNavigate, useSearchParams } from "react-router"
import {
  useConformityCounts,
  useConformityList,
  useConformityRegulationOptions,
  useDeleteConformityDeclaration,
  type ConformityListItem,
  type ConformityListParams,
  type ConformityScope,
  type ConformityStatusFilter,
} from "../../../api/conformityApi"
import { useCustomers } from "../../../api/customerRequestsApi"
import { useMcTableColumnWidths } from "../../../hooks/useMcTableColumnWidths"
import { dateFnsLocale } from "../../../utils/date"
import {
  normalizeFrameworkCode,
  regulationLabel,
  regulationShortLabel,
} from "../../../utils/regulationLabel"
import { McPill } from "../../shared/McPill"
import { McTable, McTd, McTr } from "../../shared/McTable"
import { OverflowChip } from "../../shared/OverflowChip"
import { StatGrid } from "../../shared/StatGrid"

const MC_BASE = "/material-compliance"
const PAGE_SIZE = 25
const COLUMN_IDS = [
  "reference",
  "subject",
  "scope",
  "framework",
  "status",
  "created",
  "actions",
]

// Subject gives some room to Framework so one chip plus "+N" always fits;
// the other columns stay flexible and share the rest of the table width.
// Actions must hold "Create v2 draft" + "Open" + delete on a legacy row; the
// German labels are wider, and at 240 the delete button was clipped.
const CONFORMITY_INBOX_DEFAULT_COLUMN_WIDTHS = {
  subject: 300,
  framework: 200,
  actions: 300,
}
// A drag cannot shrink the actions column below the legacy row's buttons.
const CONFORMITY_INBOX_MIN_COLUMN_WIDTHS = {
  actions: 280,
}

/** Conformity → Inbox — the landing page for the Conformity tab.
 *
 *  Mirrors the CustomerRequests InboxView so the two Customer Studio tabs
 *  share their visual grammar (StatGrid + toolbar + McTable + pagination).
 *  Reads paginated + filterable list from `useConformityList` and four-bucket
 *  counts from `useConformityCounts`. Post-Generate the wizard redirects to
 *  this same page with `?ids=…` so the just-generated batch is pre-filtered
 *  — same URL surface, no separate "review" screen. */
// The four values the Conformity dashboard widget deep-links with (see
// ConformityWidget's ROWS). `needs_review` is not a real declaration status —
// it's the SVHC subset of `draft` rows (see ConformityCountsOut.needsReview /
// flagged_svhc in mc_customer_dashboard_repository) — so it can't be forwarded
// to the backend's `status` filter as-is.
type ConformityUrlStatus = "draft" | "needs_review" | "approved" | "issued"
const VALID_URL_STATUSES = new Set<string>([
  "draft",
  "needs_review",
  "approved",
  "issued",
])
// Localized — was hardcoded English strings shown next to the translated
// MC_DECL_FILTER_PREFIX ("Filtered: Needs review" even in DE), see Finding 5.
const STATUS_CHIP_LABEL_KEYS: Record<ConformityUrlStatus, TranslationKeys> = {
  draft: "MC_CSD_CHIP_DRAFT",
  needs_review: "MC_CSD_CHIP_NEEDS_REVIEW",
  approved: "MC_CSD_CHIP_APPROVED",
  issued: "MC_CSD_CHIP_ISSUED",
}

/** Seed the real `filterStatus` facet state from `?status=`. `needs_review`
 *  widens to `draft` (its real backend superset) — the extra narrowing to
 *  only the SVHC-flagged rows happens client-side via `needsReviewOnly`. */
function initialStatusFromUrl(raw: string | null): ConformityStatusFilter | "" {
  if (raw === "needs_review") return "draft"
  if (raw === "draft" || raw === "approved" || raw === "issued") return raw
  return ""
}

export function ConformityInboxView() {
  const navigate = useNavigate()
  const { translate, currentLanguage } = useTranslation()
  const [searchParams, setSearchParams] = useSearchParams()

  // ?ids=… narrows the list to a specific batch (from Generate). Debounced
  // separately from user-typed search since it's a URL-driven param.
  const batchIds = useMemo(
    () =>
      searchParams
        .getAll("ids")
        .flatMap((s) => s.split(","))
        .filter(Boolean),
    [searchParams],
  )

  // Dashboard deep-link: ?status=draft|needs_review|approved|issued. Read
  // directly off the URL every render (like the supplier DeclarationList's
  // `lifecycle` param) so the chip and its clear affordance always reflect
  // what's actually in the address bar — a malformed/unknown value is treated
  // as absent rather than guessed at.
  const qStatusRaw = searchParams.get("status") ?? ""
  const qStatus = VALID_URL_STATUSES.has(qStatusRaw) ? qStatusRaw : ""
  const hasUrlFilter = !!qStatus

  const [searchInput, setSearchInput] = useState("")
  const [search, setSearch] = useState("")
  const [filterCustomer, setFilterCustomer] = useState("")
  const [filterScope, setFilterScope] = useState<ConformityScope | "">("")
  const [filterStatus, setFilterStatus] = useState<ConformityStatusFilter | "">(
    () => initialStatusFromUrl(searchParams.get("status")),
  )
  // Server-side narrowing for the `needs_review` deep link — `?status=
  // needs_review` widens the server `status` call to "draft" (its real
  // backend superset) AND sets `needsReview=true` so the server itself
  // returns only the SVHC-flagged rows (no client-side re-filtering, so
  // `total`/pagination are exact). Seeded once on mount, like `filterStatus`;
  // the Status facet's onSelectionChange and resetFilters both clear it so a
  // manual re-pick of the Status filter isn't silently re-narrowed by a stale
  // deep link.
  const [needsReviewOnly, setNeedsReviewOnly] = useState(
    () => searchParams.get("status") === "needs_review",
  )
  // Server-side narrowing for the "Issued this quarter" dashboard deep link —
  // the widget's href carries an explicit `issued_period=current_quarter`
  // query param (distinct from `needs_review`, which has no URL param of its
  // own and is inferred purely from `status=needs_review`) because "issued"
  // is a real backend status on its own: a manual Status-facet pick of
  // "Issued" must still show every issued declaration ever, not just this
  // quarter's.
  const [issuedPeriodOnly, setIssuedPeriodOnly] = useState(
    () => searchParams.get("issued_period") === "current_quarter",
  )
  const [filterFramework, setFilterFramework] = useState("")
  const [pendingDelete, setPendingDelete] = useState<ConformityListItem | null>(
    null,
  )
  const deleteDeclaration = useDeleteConformityDeclaration()
  const [page, setPage] = useState(1)

  // Keep filterStatus/needsReviewOnly/issuedPeriodOnly in sync with
  // `?status=`/`?issued_period=` after mount (browser back/forward, future
  // in-app deep links) — the useState initializers above only seed them once,
  const [appliedStatusParam, setAppliedStatusParam] = useState(
    searchParams.get("status"),
  )
  const [appliedIssuedPeriodParam, setAppliedIssuedPeriodParam] = useState(
    searchParams.get("issued_period"),
  )
  const rawStatusParam = searchParams.get("status")
  const rawIssuedPeriodParam = searchParams.get("issued_period")
  if (
    rawStatusParam !== appliedStatusParam ||
    rawIssuedPeriodParam !== appliedIssuedPeriodParam
  ) {
    setAppliedStatusParam(rawStatusParam)
    setAppliedIssuedPeriodParam(rawIssuedPeriodParam)
    setFilterStatus(initialStatusFromUrl(rawStatusParam))
    setNeedsReviewOnly(rawStatusParam === "needs_review")
    setIssuedPeriodOnly(rawIssuedPeriodParam === "current_quarter")
    setPage(1)
  }

  const stripStatusUrlParam = () => {
    setAppliedStatusParam(null)
    setAppliedIssuedPeriodParam(null)
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        next.delete("status")
        next.delete("issued_period")
        return next
      },
      { replace: true },
    )
  }

  const clearStatusUrlFilter = () => {
    stripStatusUrlParam()
    setFilterStatus("")
    setNeedsReviewOnly(false)
    setIssuedPeriodOnly(false)
    setPage(1)
  }

  // The KPI card matching the status filter in effect, however it was set
  // (card, deep link or Status facet). needs_review is narrower than its
  // draft superset, so only its own card lights up.
  const activeStatusCard = needsReviewOnly ? "needs_review" : filterStatus

  const toggleStatusCard = (status: ConformityUrlStatus) => {
    if (activeStatusCard === status) {
      clearStatusUrlFilter()
      return
    }
    // Written to the URL like a dashboard deep link: the sync above applies
    // it and the chip shows it. A stale issued_period would narrow "Issued"
    // to this quarter, so it goes too.
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        next.set("status", status)
        next.delete("issued_period")
        return next
      },
      { replace: true },
    )
  }

  const [columnWidths, setColumnWidths] = useMcTableColumnWidths(
    "conformityInboxV4",
    CONFORMITY_INBOX_DEFAULT_COLUMN_WIDTHS,
  )

  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput)
      setPage(1)
    }, 300)
    return () => clearTimeout(t)
  }, [searchInput])

  const params: ConformityListParams = useMemo(
    () => ({
      ids: batchIds.length > 0 ? batchIds : undefined,
      status: filterStatus || undefined,
      needsReview: needsReviewOnly || undefined,
      issuedPeriod:
        filterStatus === "issued" && issuedPeriodOnly
          ? "current_quarter"
          : undefined,
      scope: filterScope || undefined,
      customerId: filterCustomer || undefined,
      framework: filterFramework || undefined,
      search: search || undefined,
      page,
      pageSize: PAGE_SIZE,
    }),
    [
      batchIds,
      filterStatus,
      needsReviewOnly,
      issuedPeriodOnly,
      filterScope,
      filterCustomer,
      filterFramework,
      search,
      page,
    ],
  )

  const { data: counts } = useConformityCounts()
  const { data: pageData, isLoading } = useConformityList(params)
  const { data: customers = [] } = useCustomers()
  // TUR-1733: a declaration declares its picked regulations, which may lie
  // outside the org set, so the filter offers every regulation a declaration can
  // carry (the org set plus the declarable ones). The row chips take the short
  // label, as the picker's do; the filter has room for the full one.
  const { data: regulationOptions = [] } = useConformityRegulationOptions(null)
  const frameworkOptions = regulationOptions
    .filter((o) => o.inOrgSet || o.pickable)
    .map((o) => ({
      label: regulationLabel(normalizeFrameworkCode(o.key), o.key, translate),
      value: o.key,
    }))
  const frameworkChipLabel = (key: string) =>
    regulationShortLabel(normalizeFrameworkCode(key), key, translate)

  // The server now applies needsReview/issuedPeriod filtering — `items` and
  // `total` are already the exact filtered population, so no client-side
  // re-filtering or pagination caveat is needed (see Fix B).
  const rows = pageData?.items ?? []
  const total = pageData?.total ?? 0
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))

  function openEditor(row: ConformityListItem) {
    // Preserve the current batch context (if any) so the editor's pager
    // walks the same visible batch, matching the pre-restructure UX.
    const batch = batchIds.length > 0 ? `?batch=${batchIds.join(",")}` : ""
    navigate(`${MC_BASE}/customer-studio/conformity/edit/${row.id}${batch}`)
  }

  const anyFilterActive =
    !!filterCustomer ||
    !!filterScope ||
    !!filterStatus ||
    !!filterFramework ||
    !!search ||
    batchIds.length > 0

  function resetFilters() {
    setFilterCustomer("")
    setFilterScope("")
    setFilterStatus("")
    setFilterFramework("")
    setSearchInput("")
    setSearch("")
    setNeedsReviewOnly(false)
    setIssuedPeriodOnly(false)
    // The generic reset also supersedes any active `?status=` deep link —
    // strip it so the chip doesn't linger after everything else has cleared.
    stripStatusUrlParam()
    setPage(1)
    if (batchIds.length > 0) {
      navigate(`${MC_BASE}/customer-studio/conformity`)
    }
  }

  return (
    <div className="flex flex-col gap-[18px]">
      {/* Four buckets — mutually exclusive lifecycle groups. "Issued" merges
          downloaded + sent so the counter reflects "committed to the customer"
          regardless of channel; the table row still shows Downloaded / Sent
          verbatim so audit fidelity is preserved. Each card toggles its
          bucket as the status filter. */}
      <StatGrid
        items={[
          {
            label: translate("MC_CONF_STATUS_DRAFT"),
            value: (counts?.needsReview ?? 0) + (counts?.ready ?? 0),
            icon: Pencil,
            onClick: () => toggleStatusCard("draft"),
            pressed: activeStatusCard === "draft",
          },
          {
            label: translate("MC_CONF_STATUS_NEEDS_REVIEW"),
            value: counts?.needsReview ?? 0,
            icon: AlertTriangle,
            tone: "warning",
            onClick: () => toggleStatusCard("needs_review"),
            pressed: activeStatusCard === "needs_review",
          },
          {
            label: translate("MC_CONF_STATUS_APPROVED"),
            value: counts?.approved ?? 0,
            icon: CheckCircle,
            onClick: () => toggleStatusCard("approved"),
            pressed: activeStatusCard === "approved",
          },
          {
            label: translate("MC_CONF_STATUS_ISSUED"),
            value: (counts?.downloaded ?? 0) + (counts?.sent ?? 0),
            icon: Send,
            onClick: () => toggleStatusCard("issued"),
            pressed: activeStatusCard === "issued",
          },
        ]}
      />

      {/* Batch banner — surfaces when the URL has ?ids=… (post-Generate). */}
      {batchIds.length > 0 && (
        <div className="flex items-center gap-2 rounded-md border border-yellow1/40 bg-yellow1/6 px-3 py-2 text-[12px] text-yellow1">
          <Clock size={13} />
          {translate("MC_CONF_BATCH_BANNER", { n: batchIds.length })}
          <button
            type="button"
            onClick={resetFilters}
            className="ml-auto text-[11.5px] font-medium hover:text-white"
          >
            {translate("MC_CONF_BATCH_VIEW_ALL")}
          </button>
        </div>
      )}

      {/* Toolbar — matches the Customer Requests inbox grammar exactly. */}
      <div className="flex flex-wrap items-center gap-2">
        <SearchField
          value={searchInput}
          onChange={setSearchInput}
          placeholder={translate("MC_CONF_SEARCH_PLACEHOLDER")}
          onClear={() => setSearchInput("")}
        />
        <FacetedFilter
          title={translate("MC_CONF_FILTER_CUSTOMERS")}
          options={customers.map((c) => ({ label: c.name, value: c.id }))}
          selected={filterCustomer ? [filterCustomer] : []}
          onSelectionChange={(vals) => {
            setFilterCustomer(vals[vals.length - 1] ?? "")
            setPage(1)
          }}
        />
        <FacetedFilter
          title={translate("MC_CONF_FILTER_SCOPE")}
          options={[
            {
              label: translate("MC_CS_CONF_GEN_SCOPE_BY_CUSTOMER"),
              value: "customer",
            },
            {
              label: translate("MC_CS_CONF_GEN_SCOPE_BY_PRODUCT"),
              value: "product",
            },
            {
              label: translate("MC_CS_CONF_GEN_SCOPE_BY_ARTICLE"),
              value: "article",
            },
            {
              label: translate("MC_CS_CONF_V2_SCOPE_GENERAL_SHORT"),
              value: "general",
            },
          ]}
          selected={filterScope ? [filterScope] : []}
          onSelectionChange={(vals) => {
            setFilterScope((vals[vals.length - 1] as ConformityScope) ?? "")
            setPage(1)
          }}
        />
        <FacetedFilter
          title={translate("MC_CONF_FILTER_STATUS")}
          options={[
            { label: translate("MC_CONF_STATUS_DRAFT"), value: "draft" },
            { label: translate("MC_CONF_STATUS_APPROVED"), value: "approved" },
            { label: translate("MC_CONF_STATUS_ISSUED"), value: "issued" },
          ]}
          selected={filterStatus ? [filterStatus] : []}
          onSelectionChange={(vals) => {
            setFilterStatus(
              (vals[vals.length - 1] as ConformityStatusFilter) ?? "",
            )
            // A manual re-pick overrides the `?status=` deep link — both its
            // server-side needsReview/issuedPeriod narrowing AND the URL
            // params themselves, so the chip (derived from the URL) doesn't
            // go stale showing a filter that's no longer what's applied.
            setNeedsReviewOnly(false)
            setIssuedPeriodOnly(false)
            stripStatusUrlParam()
            setPage(1)
          }}
        />
        <FacetedFilter
          title={translate("MC_CS_CONF_GEN_FRAMEWORK")}
          options={frameworkOptions}
          selected={filterFramework ? [filterFramework] : []}
          onSelectionChange={(vals) => {
            setFilterFramework(vals[vals.length - 1] ?? "")
            setPage(1)
          }}
        />
        {anyFilterActive && (
          <Button
            variant="ghost"
            size="sm"
            className="h-8 text-[12px] text-grey5 hover:text-white"
            onClick={resetFilters}
          >
            {translate("MC_CONF_FILTER_RESET")}
          </Button>
        )}
      </div>

      {/* Dashboard deep-link filter chip — mirrors the supplier
          DeclarationList's `hasUrlFilter` chip pattern exactly. */}
      {hasUrlFilter && (
        <div className="flex min-w-0 items-center gap-1.5 text-[11.5px] text-grey5">
          <Filter size={11} className="shrink-0" />
          <span className="truncate">
            {translate("MC_DECL_FILTER_PREFIX")}:{" "}
            {translate(STATUS_CHIP_LABEL_KEYS[qStatus as ConformityUrlStatus])}
          </span>
          <span className="text-grey4">·</span>
          <button
            type="button"
            onClick={clearStatusUrlFilter}
            className="shrink-0 text-grey5 underline-offset-2 transition-colors hover:text-white hover:underline"
          >
            {translate("MC_DECL_FILTER_CLEAR")}
          </button>
        </div>
      )}

      <McTable
        headers={[
          translate("MC_DECL_COL_REFERENCE"),
          translate("MC_CONF_COL_SUBJECT"),
          translate("MC_CONF_FILTER_SCOPE"),
          translate("MC_CS_CONF_GEN_FRAMEWORK"),
          translate("MC_CONF_FILTER_STATUS"),
          translate("MC_CONF_COL_CREATED"),
          "",
        ]}
        columnIds={COLUMN_IDS}
        columnWidths={columnWidths}
        onColumnWidthsChange={setColumnWidths}
        minColumnWidths={CONFORMITY_INBOX_MIN_COLUMN_WIDTHS}
      >
        <tbody>
          {rows.map((r) => (
            <McTr key={r.id} onClick={() => openEditor(r)}>
              {/* Chat names a declaration by its reference; without the column
                  the user has nothing on screen to match that against. v2 rows
                  carry their revision next to the reference. */}
              <McTd variant="muted" mono>
                {r.reference ??
                  (r.isV2 ? translate("MC_CONF_V2_EDIT_PILL_DRAFT") : "—")}
                {r.isV2 && r.reference != null && r.revision != null && (
                  <span className="ml-1.5 text-[10.5px] text-grey5">
                    {translate("MC_CONF_V2_REV_SHORT", {
                      n: String(r.revision).padStart(2, "0"),
                    })}
                  </span>
                )}
              </McTd>
              <McTd variant="primary">
                <div className="flex flex-col">
                  <span className="flex items-center gap-2 text-white">
                    {subjectLine(r, translate)}
                    {/* SVHC verdict badge — only shown on drafts. Approved /
                        downloaded rows suppress it because a human has already
                        signed off on the finding; showing "Needs review" on
                        those would misleadingly re-open a closed decision. */}
                    {r.status === "draft" && r.needsReview && (
                      // Inline styles because arbitrary-opacity Tailwind
                      // (bg-red-400/[0.14]) has been purged out of this build
                      // in the past — hex constants render reliably.
                      <span
                        className="inline-flex items-center gap-1 rounded-full border px-1.5 py-px text-[10px] font-semibold"
                        style={{
                          color: "#f87171",
                          borderColor: "rgba(239, 68, 68, 0.55)",
                          backgroundColor: "rgba(239, 68, 68, 0.14)",
                        }}
                      >
                        <AlertTriangle size={9} />
                        {translate("MC_CONF_PILL_CONTAINS_SVHC")}
                      </span>
                    )}
                    {r.hasGap && (
                      // Yellow "Incomplete data" pill — surfaces on every
                      // status (not just draft) because a partial-data
                      // declaration stays partial even after approval /
                      // download / send. The reviewer needs the signal
                      // for as long as the row exists.
                      <span
                        className="inline-flex items-center gap-1 rounded-full border px-1.5 py-px text-[10px] font-semibold"
                        style={{
                          color: "#f59e0b",
                          borderColor: "rgba(245, 158, 11, 0.55)",
                          backgroundColor: "rgba(245, 158, 11, 0.14)",
                        }}
                      >
                        <AlertTriangle size={9} />
                        {translate("MC_CONF_PILL_INCOMPLETE_DATA")}
                      </span>
                    )}
                  </span>
                  {r.scope === "customer" && r.customerName && (
                    <span className="mt-0.5 text-[11px] text-grey5">
                      {r.customerName}
                    </span>
                  )}
                </div>
              </McTd>
              <McTd variant="muted" className="whitespace-nowrap">
                {scopeLabel(r.scope, translate)}
                {r.isV2 && r.articleCount > 0 && (
                  <span className="ml-1 text-[11px] text-grey5">
                    · {r.articleCount}
                  </span>
                )}
              </McTd>
              <McTd variant="muted" className="whitespace-nowrap">
                {r.isV2 ? (
                  <FrameworkChips
                    labels={r.frameworkKeys.map(frameworkChipLabel)}
                  />
                ) : (
                  <span className="flex max-w-full min-w-0 items-center gap-1">
                    {r.framework ? (
                      <FrameworkChip
                        label={regulationLabel(
                          r.frameworkCode ??
                            normalizeFrameworkCode(r.framework),
                          r.framework,
                          translate,
                        )}
                      />
                    ) : (
                      "—"
                    )}
                    {
                      <FrameworkChip
                        label={translate("MC_CONF_V2_LEGACY_CHIP")}
                        outline
                      />
                    }
                  </span>
                )}
              </McTd>
              <McTd>
                <StatusPill row={r} />
              </McTd>
              <McTd variant="muted">
                {r.generatedAt ? (
                  // Absolute date is the primary read (auditors + support
                  // want the actual day, not "5 days ago"); relative time
                  // goes under it as a freshness cue.
                  <div className="flex flex-col">
                    <span className="font-mono text-white">
                      {format(new Date(r.generatedAt), "dd MMM yyyy", {
                        locale: dateFnsLocale(currentLanguage),
                      })}
                    </span>
                    <span className="text-[11px] text-grey5">
                      {formatDistanceToNow(new Date(r.generatedAt), {
                        addSuffix: true,
                        locale: dateFnsLocale(currentLanguage),
                      })}
                    </span>
                  </div>
                ) : (
                  "—"
                )}
              </McTd>
              <McTd className="text-right whitespace-nowrap">
                <div className="inline-flex items-center justify-end gap-1">
                  {!r.isV2 && (
                    // Legacy v1 rows are frozen; the way forward is a fresh v2
                    // draft over the same subject (design: legacy state).
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 px-2 text-[11.5px] text-grey5 hover:text-white"
                      onClick={(e) => {
                        e.stopPropagation()
                        navigate(createV2DraftPath(r))
                      }}
                    >
                      {translate("MC_CONF_V2_CREATE_DRAFT")}
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2 text-[11.5px] text-grey5 hover:text-white"
                    onClick={(e) => {
                      e.stopPropagation()
                      openEditor(r)
                    }}
                  >
                    <Pencil size={11} className="mr-1" />
                    {translate("MC_CONF_BTN_OPEN")}
                  </Button>
                  {/* Drafts only: an issued declaration keeps its reference and
                      frozen PDF (the backend refuses with NOT_DRAFT anyway). */}
                  {r.status === "draft" && (
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={`${translate("MC_CONF_V2_DELETE")} ${subjectLine(r, translate)}`}
                      className="h-7 w-7 text-grey5 hover:bg-red1/10 hover:text-red1"
                      onClick={(e) => {
                        e.stopPropagation()
                        setPendingDelete(r)
                      }}
                    >
                      <Trash2 size={12} />
                    </Button>
                  )}
                </div>
              </McTd>
            </McTr>
          ))}
          {!rows.length && (
            <McTr>
              <McTd variant="muted" colSpan={7} className="py-6 text-center">
                {isLoading
                  ? translate("MC_CS_CONF_REVIEW_LOADING")
                  : anyFilterActive
                    ? translate("MC_CONF_EMPTY_FILTERED")
                    : translate("MC_CONF_EMPTY_NONE")}
              </McTd>
            </McTr>
          )}
        </tbody>
      </McTable>

      {pendingDelete && (
        <Dialog open onOpenChange={(open) => !open && setPendingDelete(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{translate("MC_CONF_V2_DELETE_TITLE")}</DialogTitle>
              <p className="text-[12.5px] leading-relaxed text-grey5">
                {translate("MC_CONF_V2_DELETE_BODY", {
                  subject: subjectLine(pendingDelete, translate),
                })}
              </p>
            </DialogHeader>
            <DialogFooter>
              <Button
                variant="outline"
                className="h-9 text-[13px]"
                onClick={() => setPendingDelete(null)}
              >
                {translate("MC_CS_CONF_EDIT_CANCEL")}
              </Button>
              <Button
                variant="destructive"
                className="h-9 text-[13px] font-semibold text-white disabled:opacity-40"
                disabled={deleteDeclaration.isPending}
                onClick={async () => {
                  const row = pendingDelete
                  setPendingDelete(null)
                  try {
                    await deleteDeclaration.mutateAsync(row.id)
                    toast.success(translate("MC_CONF_V2_DELETE_TOAST"))
                  } catch {
                    toast.error(translate("MC_CS_CONF_GEN_TOAST_ERR"))
                  }
                }}
              >
                {translate("MC_CONF_V2_DELETE")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Pagination */}
      <div className="flex items-center justify-between text-[12px] text-grey5">
        <span>
          {translate("MC_CONF_PAGINATION", {
            page: total === 0 ? 0 : page,
            totalPages,
            total,
          })}
        </span>
        <div className="flex items-center gap-1.5">
          <Button
            variant="outline"
            size="sm"
            className="h-7 border-grey4 px-2"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            <ChevronLeft size={14} />
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="h-7 border-grey4 px-2"
            disabled={page >= totalPages || total === 0}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          >
            <ChevronRight size={14} />
          </Button>
        </div>
      </div>
    </div>
  )
}

// ─── Helpers ────────────────────────────────────────────────────────────────

/** Subject "identity" column — different fields matter per scope. */
function FrameworkChip({
  label,
  outline,
}: {
  label: string
  outline?: boolean
}) {
  // The chip may shrink below its label (long legacy names such as
  // "REACH SVHC (Candidate List, Annex XIV)"); the text truncates with an
  // ellipsis and the full label stays reachable via the title.
  return (
    <span
      title={label}
      className={
        outline
          ? "inline-flex h-[20px] shrink-0 items-center rounded-full border border-grey4 px-2 text-[10.5px] font-medium text-grey5"
          : "inline-flex h-[20px] max-w-full min-w-0 items-center rounded-full border border-yellow1/50 bg-yellow1/10 px-2 text-[10.5px] font-medium text-yellow1"
      }
    >
      <span className="min-w-0 truncate">{label}</span>
    </span>
  )
}

/** First framework as a chip, the rest folded into a "+N" chip whose hover
 *  lists them as chips. A v2 row can carry seven frameworks; laying them all
 *  out overflowed the cell. */
function FrameworkChips({ labels }: { labels: string[] }) {
  if (labels.length === 0) return <>—</>
  const [first, ...rest] = labels
  return (
    <span className="flex max-w-full min-w-0 items-center gap-1.5">
      <FrameworkChip label={first} />
      {rest.length > 0 && (
        <OverflowChip count={rest.length} testId="framework-more">
          {rest.map((label) => (
            <FrameworkChip key={label} label={label} />
          ))}
        </OverflowChip>
      )}
    </span>
  )
}

/** A fresh v2 draft over the legacy row's subject: customer rows land on the
 *  Customer tab preselected; product/article rows on the Products tab. */
function createV2DraftPath(r: ConformityListItem): string {
  const base = `${MC_BASE}/customer-studio/conformity/new`
  if (r.scope === "customer" && r.customerId) {
    return `${base}?scope=customer&customerId=${r.customerId}`
  }
  if (r.scope === "product" && r.productId) {
    return `${base}?scope=products&productId=${r.productId}`
  }
  if (r.scope === "article" && r.articleId) {
    return `${base}?scope=article&articleId=${r.articleId}`
  }
  return base
}

// General rows carry a free-text product family description instead of a
// product/article: truncated so a long sentence doesn't blow out the row.
const GENERAL_SUBJECT_MAX = 60

function subjectLine(
  r: ConformityListItem,
  translate: ReturnType<typeof useTranslation>["translate"],
): string {
  if (r.scope === "general") {
    const text = r.productDescription ?? ""
    // An empty description is the DEFAULT for a general declaration, not missing
    // data: it covers everything the issuer makes. Naming that is clearer than a
    // placeholder glyph, which reads as "we failed to load something".
    if (!text) return translate("MC_CS_CONF_V2_GENERAL_ALL_PRODUCTS")
    return text.length > GENERAL_SUBJECT_MAX
      ? `${text.slice(0, GENERAL_SUBJECT_MAX)}...`
      : text
  }
  if (r.scope === "article") {
    return [r.articleNumber, r.articleName].filter(Boolean).join(" · ") || "—"
  }
  return [r.productNumber, r.productName].filter(Boolean).join(" · ") || "—"
}

function scopeLabel(
  scope: ConformityScope,
  translate: ReturnType<typeof useTranslation>["translate"],
): string {
  if (scope === "customer") return translate("MC_CS_CONF_GEN_SCOPE_BY_CUSTOMER")
  if (scope === "general") return translate("MC_CS_CONF_V2_SCOPE_GENERAL_SHORT")
  if (scope === "product") return translate("MC_CS_CONF_GEN_SCOPE_BY_PRODUCT")
  return translate("MC_CS_CONF_GEN_SCOPE_BY_ARTICLE")
}

/** Pure workflow state — draft / approved / downloaded. The SVHC verdict
 *  (needs_review) is a *separate* signal rendered as a badge next to the
 *  Subject cell, not folded into this pill: once a human approves, the SVHC
 *  finding is signed off, not a call to action, and mixing it here would
 *  keep re-opening a closed decision. */
function StatusPill({ row }: { row: ConformityListItem }) {
  const { translate } = useTranslation()
  if (row.status === "sent") {
    return (
      <McPill tone="success" icon={Send}>
        {translate("MC_CONF_STATUS_SENT")}
      </McPill>
    )
  }
  if (row.status === "downloaded") {
    return (
      <McPill tone="muted" icon={Download}>
        {translate("MC_CONF_STATUS_DOWNLOADED")}
      </McPill>
    )
  }
  if (row.status === "approved") {
    return (
      <McPill tone="info" icon={CheckCircle}>
        {translate("MC_CONF_STATUS_APPROVED")}
      </McPill>
    )
  }
  return (
    <McPill tone="muted" icon={Pencil}>
      {translate("MC_CONF_STATUS_DRAFT")}
    </McPill>
  )
}
